// frontend/src/pages/Home.jsx
import React from "react";
import { Link } from "react-router-dom";
import Screen from "../uizard/ui/Screen";
import Faq from "../components/Faq";
import RecentCarousel from "../components/RecentCarousel";

export default function Home() {
  // Design tokens (tweak here to fine-tune brand color)
  const brand = {
    purple: "#6D5EF9",
    purpleDark: "#5B4EE8",
    purpleTint: "#F3F0FF",
    purpleBorder: "#E7E2FF",
    neutralBorder: "#E5E7EB",
    neutralText: "#111827",
    gradient: "linear-gradient(135deg, #1E3A8A 0%, #6D28D9 100%)",
  };

  const btnBase = {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "12px 18px",
    borderRadius: 12,
    fontWeight: 600,
    fontSize: 16,
    lineHeight: "24px",
    textDecoration: "none",
    transition: "all 160ms ease-in-out",
    userSelect: "none",
  };

  const btnPrimary = {
    ...btnBase,
    background: brand.purple,
    color: "#fff",
    boxShadow: "0 6px 16px rgba(109,94,249,0.25)",
  };

  const btnPrimaryHover = { background: brand.purpleDark };

  const btnSecondary = {
    ...btnBase,
    background: brand.purpleTint,
    color: brand.purple,
    border: `1px solid ${brand.purpleBorder}`,
  };

  // Collapsible state – default collapsed so the carousel stays visible
  const [howOpen, setHowOpen] = React.useState(false);

  /* ---------------------- Production FAQ content ---------------------- */
const FAQ_GROUPS = [
  {
    title: "Getting started",
    items: [
      {
        q: "What is ArtiBytes?",
        a: "ArtiBytes is an AI image generation platform where you buy credits, generate images, and (optionally) use Creator Styles trained from creator-submitted style packs.",
      },
      {
        q: "How do I sign in?",
        a: "ArtiBytes currently supports Google sign-in. You can create an account or sign in using your Google identity.",
      },
      {
        q: "Do I need to install anything?",
        a: "No. ArtiBytes runs in your browser. You just sign in and start generating.",
      },
    ],
  },

  {
    title: "Credits & payments",
    items: [
      {
        q: "What are credits?",
        a: "Credits are the unit used to generate images on ArtiBytes. Each generation action consumes credits based on the action performed.",
      },
      {
        q: "How many credits does an image cost?",
        a: "Each image generation costs 1 credit. Re-generating an image also costs 1 credit.",
      },
      {
        q: "Do creators pay full credits when testing their own Styles?",
        a: "No. When testing adjustments to their own Style, creators are charged a reduced rate of 0.5 credit per generation.",
      },
      {
        q: "How do I buy credits?",
        a: "You buy credits through Stripe checkout using the payment methods shown in-app. Credits are added to your account automatically after successful payment.",
      },
      {
        q: "I paid but didn’t get credits. What should I do?",
        a: "Refresh the page and check your balance. If credits still don’t appear, contact support with your account email and purchase time so we can verify and correct the balance.",
      },
      {
        q: "Are credit purchases refundable?",
        a: "Credit purchases are generally final once a generation is successfully delivered. If a technical error prevents delivery, we may re-deliver or credit your account after review.",
      },
    ],
  },

  {
    title: "Images, ownership & commercial use",
    items: [
      {
        q: "Do I own the images I generate?",
        a: "Subject to our Terms and applicable law, you own the images you generate (to the extent permitted) and may use them commercially. You are responsible for how you use outputs, including checking rights for any real-world brands or third-party IP you add in prompts.",
      },
      {
        q: "If I use a Creator Style, can the creator claim my outputs?",
        a: "No. Creators do not own or control your outputs simply because you used their Style. You may use outputs commercially under our Terms.",
      },
      {
        q: "Do I lose royalties if users delete images?",
        a: "No, never. Royalties are earned instantly when images are generated and are permanently yours. Note: The Total earned stat may change after clicking Refresh Data if generations were deleted, but your Available balance  never decreases. Once earned, royalties are yours to keep forever.",
      },
      {
        q: "Are generated images unique?",
        a: "Yes, each generation is unique. However, using similar prompts with the same style can produce visually similar outputs. Custom styles help differentiate your work. You own your generated images, but ensure your prompts respect third-party intellectual property.",
      },
    ],
  },

  {
    title: "Image disputes (credit refund)",
    items: [
      {
        q: "How do I dispute an image?",
        a: "If you believe an image materially fails to match a Style’s description or a technical issue affected the result, you can submit a dispute from the image view inside the app.",
      },
      {
        q: "What happens after I dispute an image?",
        a: "The dispute is reviewed in-platform. The Style owner may dismiss it as invalid or approve a refund of the credit used for that image. ArtiBytes may also review disputes to ensure fairness and prevent abuse.",
      },
      {
        q: "Do disputes give cash refunds?",
        a: "No. Disputes are resolved by refunding a credit to your account (when approved).",
      },
    ],
  },

  {
    title: "Royalty Program",
    items: [
      {
        q: "What is the Royalty Program?",
        a: "Creators can submit a style pack for training. If approved, ArtiBytes hosts the Style and users can generate with it. Creators earn royalties from eligible paid usage under the Royalty Program rules.",
      },
      {
        q: "Who owns the trained Style?",
        a: "Creators keep ownership of their original uploaded assets. ArtiBytes owns the platform and the trained model artifacts and configurations used to operate Styles (excluding the creator’s original assets).",
      },
      {
        q: "How do royalties work?",
        a: "Royalties accrue from eligible paid usage of your Style. Royalties are calculated on net revenue after adjustments like refunds, chargebacks, taxes, and payment processing fees, as described in the Creator Agreement.",
      },
      {
        q: "How do payouts work?",
        a: "Royalties accrue to your ArtiBytes account balance and become payable once you meet any minimum threshold and complete verification. Payouts use the payout methods supported by ArtiBytes at the time of payment.",
      },
      {
        q: "Can my Style be removed?",
        a: "Yes. ArtiBytes may review, suspend, or remove Styles for policy, safety, legal, or operational reasons.",
      },
    ],
  },

  {
    title: "Downloads & sharing",
    items: [
      {
        q: "How do I download images?",
        a: "Use Download on a result or in your Gallery to save the file to your device.",
      },
      {
        q: "What is the Home carousel?",
        a: "You can toggle Allow on Home for an image in your Gallery to feature it publicly. You can toggle it off anytime.",
      },
    ],
  },

  {
    title: "Ownership, rights & safety",
    items: [
      {
        q: "Who owns the images I generate?",
        a: "You do—subject to our Terms, Content Policy, and any third-party rights in your prompts/uploads.",
      },
      {
        q: "Can I use outputs commercially?",
        a: "Yes, provided you comply with applicable laws and third-party rights. Certain subjects may require permission or licensing.",
      },
      {
        q: "What content is not allowed?",
        a: "No illegal content, no exploitation, and no third-party IP misuse (like copyrighted characters or brands) without permission. See the Content Policy for details.",
      },
    ],
  },

  {
    title: "Data & storage",
    items: [
      {
        q: "What data do you collect?",
        a: "We collect account and identity info (Google sign-in), usage activity, technical logs, and content you upload or generate. We also store payment and receipt references for credit purchases.",
      },
      {
        q: "Do you store my generated images forever?",
        a: "Your generated images stay in your account so you can access them later. If you delete an image, it’s removed from active systems, subject to limited retention in backups or logs for legal, security, or integrity purposes.",
      },
      {
        q: "Do you sell my data?",
        a: "No. We do not sell or rent your personal data.",
      },
    ],
  },

  {
    title: "Safety & content policy",
    items: [
      {
        q: "What happens if someone uploads prohibited content as a Style pack?",
        a: "We may reject or remove Styles and take account action if submissions violate policies or law.",
      },
    ],
  },

  {
    title: "Legal",
    items: [
      {
        q: "Licenses",
        a: "By submitting style packs to the Royalty Program, you grant ArtiBytes a non-exclusive, worldwide, commercial license to train and serve your Style. Full terms are in the Terms of Service, Creator Agreement, and Content Policy.",
      },
    ],
  },
];


  return (
    <Screen>
      <StyleTag />

      {/* Hero */}
      <section>
        <h1
          className="h1"
          style={{
            background: brand.gradient,
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            backgroundClip: "text",
          }}
        >
          ArtiBytes
        </h1>
        <p className="lead">Your Style, Your Economy</p>

        {/* CTA row - REORDERED */}
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginTop: 8 }}>
          <Link
            to="/profile"
            style={btnPrimary}
            onMouseEnter={(e) => Object.assign(e.currentTarget.style, btnPrimaryHover)}
            onMouseLeave={(e) => Object.assign(e.currentTarget.style, { background: brand.purple })}
          >
            Buy Credits
          </Link>

          <Link to="/create" style={btnSecondary}>
            Start Creating
          </Link>

          <Link to="/creator" style={btnSecondary}>
            Royalty Program
          </Link>
        </div>
      </section>

      {/* How it works – collapsible with animated hint */}
      <section className="how-wrap">
        <button
          className="how-trigger"
          aria-expanded={howOpen}
          aria-controls="how-content"
          onClick={() => setHowOpen((v) => !v)}
          type="button"
        >
          <span className="how-title">
            How it works
            {!howOpen && <span className="how-hint"> Click to learn</span>}
          </span>
          <svg
            className={`chev ${howOpen ? "open" : ""}`}
            width="16"
            height="16"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path
              d="M6 9l6 6 6-6"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>

        {howOpen && (
          <div id="how-content" className="how-steps">
            <article className="how-card">
              <div className="how-badge">1</div>
              <div className="how-body">
                <h3>Describe your image</h3>
                <p>
                  Write a clear prompt. Add details like subject, lighting, mood, and camera/style hints.
                </p>
              </div>
            </article>

            <article className="how-card">
              <div className="how-badge">2</div>
              <div className="how-body">
                <h3>Pick a Style (optional)</h3>
                <p>
                  Use a curated Style for a consistent look—or stick with Default for a flexible baseline.
                </p>
              </div>
            </article>

            <article className="how-card">
              <div className="how-badge">3</div>
              <div className="how-body">
                <h3>Generate & save</h3>
                <p>
                  Spend 1 credit to generate. Save, share, or re-generate with tweaks until it's perfect.
                </p>
              </div>
            </article>

            <article className="how-card">
              <div className="how-badge">4</div>
              <div className="how-body">
                <h3>Start earning royalties</h3>
                <p>
                  Submit your style and earn royalties when others use it to generate images.
                </p>
              </div>
            </article>
          </div>
        )}
      </section>

      {/* Featured Creators – no duplicate heading */}
      <section style={{ marginTop: 32 }}>
        <RecentCarousel />
      </section>

      {/* FAQ */}
      <section style={{ marginTop: 48 }}>
        <Faq
          groups={FAQ_GROUPS}
          title="FAQs"
          subtitle=""
          defaultGroup={0}
          showSearch={true}
          titleGradientCss="linear-gradient(135deg, #1E3A8A 0%, #6D28D9 100%)"
        />
      </section>
    </Screen>
  );
}

/* Local styles (collapsible + original roomy cards) */
function StyleTag() {
  return (
    <style>{`
.how-wrap{ margin-top: 28px; }

/* Trigger row */
.how-trigger{
  width:100%;
  display:flex; align-items:center; justify-content:space-between;
  padding:12px 14px;
  border:1px solid #E5E7EB; border-radius:14px;
  background:#fff; cursor:pointer;
  box-shadow:0 8px 18px rgba(17,24,39,.05);
  transition: all 0.2s ease;
}

.how-trigger:hover{
  border-color: #6D5EF9;
  box-shadow: 0 8px 24px rgba(109,94,249,0.12);
}

.how-title{
  font-weight:900; font-size:18px;
  color:#111827;
  display: flex;
  align-items: center;
  gap: 12px;
}

.how-hint{
  font-size: 14px;
  font-weight: 700;
  color: #6D5EF9;
  animation: bounce 2s ease-in-out infinite;
}

@keyframes bounce {
  0%, 20%, 50%, 80%, 100% {
    transform: translateY(0);
  }
  40% {
    transform: translateY(-8px);
  }
  60% {
    transform: translateY(-4px);
  }
}

.chev{ transition: transform .18s ease; color:#111827; }
.chev.open{ transform: rotate(180deg); }

/* Cards (collapsible content) */
.how-steps{
  margin-top:12px;
  display:grid; gap:14px; grid-template-columns: 1fr;
}
@media (min-width: 860px){
  .how-steps{ grid-template-columns: repeat(2, 1fr); }
}
@media (min-width: 1100px){
  .how-steps{ grid-template-columns: repeat(4, 1fr); }
}
.how-card{
  display:flex; gap:14px; align-items:flex-start;
  border:1px solid #E5E7EB; background:#fff;
  border-radius:20px; padding:18px;
  box-shadow: 0 12px 28px rgba(17,24,39,.06);
}
.how-badge{
  flex:0 0 auto;
  width:44px; height:44px; border-radius:14px;
  display:grid; place-items:center; font-weight:900; color:#fff;
  background: linear-gradient(135deg, #1E3A8A 0%, #6D28D9 100%);
  box-shadow: 0 10px 24px rgba(109,94,249,.25);
  font-size:16px;
}
.how-body h3{
  margin:0 0 6px; font-size:18px; line-height:1.25; font-weight:900; color:#111827;
}
.how-body p{
  margin:0; color:#4B5563; line-height:1.55; font-size:14.5px;
}

/* Mobile polish */
@media (max-width: 720px){
  .how-trigger{ padding:12px; }
  .how-title{ font-size:17px; }
  .how-hint{ font-size:13px; }
  .how-card{ padding:16px; border-radius:18px; }
  .how-badge{ width:40px; height:40px; font-size:15px; }
  .how-body h3{ font-size:17px; }
  .how-body p{ font-size:14px; }
}
    `}</style>
  );
}